import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { orderFilterLabels } from '@epicuro-next/constants/features/localization';
import {
  ASSOCIATED_ACCOUNT_FILTER_ID,
  ORDER_BOOK_SEARCH_URL_PARAM,
} from '@epicuro-next/constants/features/order-book';
import { AccountsService } from '@epicuro-next/data/accounts';
import { OrdersV2Service } from '@epicuro-next/data/order-book';
import {
  ActiveFilter,
  Filter,
  FilterConfig,
} from '@epicuro-next/models/filters';
import {
  FindOrdersRequestDto,
  OrderFilterId,
  SortDirection,
  SortField,
  SortRule,
} from '@epicuro-next/models/order-book';
import { fromDynamicEvent } from '@epicuro-next/platform/dynamic-actions';
import { selectLanguage } from '@epicuro-next/platform/i18n';
import {
  selectActiveAccount,
  selectActiveAccountId,
} from '@epicuro-next/state/accounts-state';
import { selectBasketAccounts } from '@epicuro-next/state/basket-state';
import { generateQueryParamsOnFilterUpdate } from '@epicuro-next/state/order-book';
import {
  selectQueryParams,
  updateQueryParameters,
} from '@epicuro-next/state/router-store';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isEqual, isNonNullish, unique } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Account } from 'graphql-api/gql-api-interfaces';
import {
  catchError,
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import {
  FiltersNamesMap,
  filtersToRequestFilters,
  getValueToDisplayNameMappers,
  isAccountFilter,
  isInvoiceCreatedFilter,
  isPlacedDateFilter,
} from '../../shared';
import { PAGE_SIZE } from '../constants';

import { getAvailableFilters } from './shared/get-available-filters/get-available-filters';
import { getInitialSelectedFiltersFromQueryParams } from './shared/get-inititial-selected-filters/get-initial-selected-filters-from-query-params';
import { getUpdatedSelectedFilters } from './shared/update-selected-filters/get-updated-selected-filters';

export interface State {
  associatedAccounts: string[] | null;
  searchQuery: string;
  availableFilters: Filter[] | null;
  selectedFilters: ActiveFilter[] | null;
  sort: SortRule | null;
  filtersNamesMap: FiltersNamesMap;
  // customized config properties
  filtersConfig: FilterConfig[] | null;
  sortMapping: Record<string, SortRule[]> | null;
  defaultSortRule: SortRule | null;
  isMultiAccountEnabled: boolean | null;
  uiFilters: Filter[] | null;
  // end customized config properties
}

const initial: State = {
  associatedAccounts: null,
  availableFilters: null,
  selectedFilters: null,
  searchQuery: '',
  sort: null,
  filtersNamesMap: {},
  filtersConfig: null,
  sortMapping: null,
  defaultSortRule: null,
  isMultiAccountEnabled: null,
  uiFilters: null,
};

@Injectable()
export class OrdersFiltersStore extends ComponentStore<State> {
  private readonly accountsService = inject(AccountsService);
  private readonly store = inject(Store);
  private readonly ordersService = inject(OrdersV2Service);
  private readonly orderFiltersConfig$ = this.select(
    ({ filtersConfig }) => filtersConfig,
  ).pipe(filter(isNonNullish));

  private readonly activeAccountId$ = this.store.pipe(
    select(selectActiveAccountId),
    filter(isNonNullish),
  );

  private readonly activeAccount$ = this.store.pipe(
    select(selectActiveAccount),
    filter(isNonNullish),
  );

  private readonly basketAccounts$ = this.store.pipe(
    select(selectBasketAccounts),
  );

  private selectAssociatedAccounts$ = this.select(
    ({ associatedAccounts }) => associatedAccounts,
  ).pipe(
    filter(isNonNullish),
    map((accounts) => ({
      id: ASSOCIATED_ACCOUNT_FILTER_ID,
      values: accounts,
    })),
  );

  private readonly filtersNamesMap$ = this.select(
    ({ filtersNamesMap }) => filtersNamesMap,
  );

  private readonly sortMapping$ = this.select(
    ({ sortMapping }) => sortMapping,
  ).pipe(filter(isNonNullish));
  private readonly defaultSortRule$ = this.select(
    ({ defaultSortRule }) => defaultSortRule,
  ).pipe(filter(isNonNullish));
  private readonly isMultiAccountEnabled$ = this.select(
    ({ isMultiAccountEnabled }) => isMultiAccountEnabled,
  ).pipe(filter(isNonNullish));

  public readonly searchQuery$ = this.select(({ searchQuery }) => searchQuery);

  private readonly availableFilters$ = this.select(
    ({ availableFilters }) => availableFilters,
  );

  private readonly filterValueDisplayNamesMappers$: Observable<
    Record<string, (name: string) => string>
  > = this.select(
    this.filtersNamesMap$,
    this.store.pipe(select(selectLanguage)),
    getValueToDisplayNameMappers,
  );

  private readonly mappedAvailableFilters$: Observable<Filter[]> =
    combineLatest([
      this.availableFilters$.pipe(filter(isNonNullish)),
      this.filterValueDisplayNamesMappers$,
    ]).pipe(
      combineLatestWith(this.orderFiltersConfig$),
      map(([[filters, valueDisplayNameMappers], filterConfiguration]) =>
        (filters ?? [])
          .map((facetItem) => {
            const filterItem = filterConfiguration.find(
              (facet) => facetItem.id === facet.id,
            ) as FilterConfig;

            return {
              ...filterItem,
              fieldDisplayName:
                orderFilterLabels[facetItem.id as OrderFilterId] ??
                facetItem.id,
              ...facetItem,
              values: facetItem.values.map((v) => {
                const mapper = valueDisplayNameMappers[facetItem.id];
                return {
                  ...v,
                  displayName: mapper ? mapper(v.name) : v.name,
                };
              }),
              id: facetItem.id,
            };
          })
          .sort((a, b) => a.order - b.order),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );

  public readonly filterChangeAvailable$ = this.select(
    this.availableFilters$.pipe(filter(isNonNullish)),
    (filters) =>
      filters
        .flatMap(({ values }) => values)
        .some(({ count }) => (count ?? 0) > 0),
  );

  public readonly orderFilters$: Observable<Filter[]> = this.select(
    this.mappedAvailableFilters$,
    (filters) =>
      filters.filter(
        (f) =>
          !isAccountFilter(f) &&
          !isInvoiceCreatedFilter(f) &&
          !isPlacedDateFilter(f),
      ),
  );

  public readonly accountFilter$ = this.select(
    this.mappedAvailableFilters$,
    (filters) => filters.find(isAccountFilter),
  );

  public readonly placedDateFilter$ = this.select(
    this.mappedAvailableFilters$,
    (filters) => filters.find(isPlacedDateFilter),
  );

  public readonly selectedFilters$ = this.select(
    ({ selectedFilters }) => selectedFilters,
  );

  public readonly filtersApplied$ = this.select(
    this.selectedFilters$,
    (filters) => !!filters?.length,
  );

  public readonly searchApplied$ = this.select(this.searchQuery$, Boolean);

  private filtersQuery$ = this.select(
    this.searchQuery$,
    this.selectedFilters$,
    this.selectAssociatedAccounts$,
    (query, selectedFilters, associatedAccounts): FindOrdersRequestDto => {
      return {
        start: 0,
        num: 0,
        query,
        filters: filtersToRequestFilters(
          (selectedFilters ?? []).concat(associatedAccounts),
        ),
      };
    },
  ).pipe(distinctUntilChanged(isEqual));

  public readonly activeFilters$ = this.select(
    this.selectedFilters$.pipe(filter(isNonNullish)),
    (filters) => filters,
  );

  public readonly sort$ = this.select(
    this.select(({ sort }) => sort),
    this.defaultSortRule$,
    (selectedSort, defaultSort) => selectedSort ?? defaultSort,
  );

  public readonly ordersQuery$: Observable<FindOrdersRequestDto> =
    combineLatest([
      // wait account preselection as it is required for orders
      this.selectedFilters$.pipe(filter(isNonNullish)),
      this.selectAssociatedAccounts$,
      this.searchQuery$,
    ]).pipe(
      map(
        ([activeFilters, associatedAccounts, query]): FindOrdersRequestDto => {
          return {
            filters: filtersToRequestFilters(
              activeFilters.concat(associatedAccounts),
            ),
            query,
            num: PAGE_SIZE,
          };
        },
      ),
      distinctUntilChanged(isEqual),
      shareReplay({ refCount: true, bufferSize: 1 }),
      takeUntil(this.destroy$),
    );

  public readonly ordersQueryWithSorting$ = this.select(
    this.ordersQuery$,
    this.sort$,
    this.sortMapping$,
    (query, sort, sortMapping) => {
      if (sort) {
        return {
          ...query,
          sorting: this.mapSortValue(sort, sortMapping),
        };
      }

      return query;
    },
  );

  private readonly selectURLQueryParams$ = this.select(
    combineLatest([this.activeFilters$, this.searchQuery$]).pipe(
      concatLatestFrom(() => this.store.select(selectQueryParams)),
    ),
    ([[filters, search], oldParams]): Params | null =>
      generateQueryParamsOnFilterUpdate(
        oldParams,
        filters.map((f) => ({
          fieldId: f.id,
          value: f.values.join('|'),
        })),
        search,
      ),
  ).pipe(distinctUntilChanged(isEqual));

  private readonly selectDefaultFilters$ = this.select(
    this.orderFiltersConfig$,
    this.activeAccountId$,
    (filtersConfig, activeAccountId) => {
      return filtersConfig
        .filter(({ isPreselected }) => isPreselected)
        .map(({ defaultValue, id }) => {
          return {
            id,
            values: [defaultValue as string],
          };
        })
        .concat({
          id: OrderFilterId.Account,
          values: [activeAccountId],
        });
    },
  );

  public readonly isResetFiltersAvailable$ = this.select(
    this.selectedFilters$,
    this.selectDefaultFilters$,
    (selectedFilters, defaultSelected) =>
      !!selectedFilters?.length && !isEqual(selectedFilters, defaultSelected),
  );

  public setSort = this.effect<{ detail: SortField }>((event$) => {
    return event$.pipe(
      concatLatestFrom(() => [this.sort$]),
      tap(([{ detail }, sort]) => {
        const newSort: SortRule = {
          field: detail,
          direction: this.toggleSortDirection(sort, detail),
        };

        this.patchState(() => ({
          sort: newSort,
        }));
      }),
    );
  })(
    fromDynamicEvent<{ detail: SortField }>(
      '[Dynamic Actions] [Order Book] Toggle Sorting',
    ),
  );

  constructor() {
    super(initial);
    this.getAssociatedAccounts();
  }

  public getFilters$ = this.effect<FindOrdersRequestDto>((query$) => {
    return query$.pipe(
      switchMap((query) => {
        return this.ordersService.getFilters(query).pipe(
          tapResponse(
            (response) =>
              this.patchState(({ selectedFilters, uiFilters }) => {
                return {
                  availableFilters: [
                    ...getAvailableFilters(
                      selectedFilters ?? [],
                      response.filters,
                    ),
                    ...(uiFilters ?? []),
                  ],
                };
              }),
            () => {
              this.store.dispatch(
                showSnackBarMessage({
                  message: $localize`:OrderBook|Failure load filters message@@loadFiltersFailureMessage:Failed to load filters`,
                  messageType: MessageType.DANGER,
                }),
              );
            },
          ),
        );
      }),
    );
  })(this.filtersQuery$);

  public readonly updateQueryOnSelectedFilterChange$ = this.effect(
    (queryParams$: Observable<Params | null>) => {
      return queryParams$.pipe(
        tap((queryParams: Params | null) => {
          this.store.dispatch(
            updateQueryParameters({
              queryParams,
              queryParamsHandling: 'merge',
            }),
          );
        }),
      );
    },
  )(this.selectURLQueryParams$);

  public getBrandNames$ = this.effect<string[]>((ids$) => {
    return ids$.pipe(
      filter((ids) => ids.length > 0),
      switchMap((ids) =>
        this.ordersService.getBrandNames(ids).pipe(
          tapResponse(
            (brandNames) => {
              this.patchState((state) => ({
                ...state,
                filtersNamesMap: {
                  ...state.filtersNamesMap,
                  [OrderFilterId.Brand]: brandNames,
                },
              }));
            },
            () => {
              this.store.dispatch(
                showSnackBarMessage({
                  message: $localize`:OrderBook|Failure load brand names message@@loadFiltersBrandNamesFailureMessage:Failed to load brand names`,
                  messageType: MessageType.DANGER,
                }),
              );
              return EMPTY;
            },
          ),
        ),
      ),
    );
  })(this.getAvailableFilterValues(OrderFilterId.Brand));

  public getAddressNames$ = this.effect<string[]>((ids$) => {
    return ids$.pipe(
      filter((ids) => ids.length > 0),
      switchMap((ids) =>
        this.ordersService.getAddressNames(ids).pipe(
          tapResponse(
            (addressNames) => {
              this.patchState((state) => ({
                ...state,
                filtersNamesMap: {
                  ...state.filtersNamesMap,
                  [OrderFilterId.Address]: addressNames,
                },
              }));
            },
            () => {
              this.store.dispatch(
                showSnackBarMessage({
                  message: $localize`:OrderBook|Failure load address names message@@loadFiltersAddressNamesFailureMessage:Failed to load address names`,
                  messageType: MessageType.DANGER,
                }),
              );
              return EMPTY;
            },
          ),
        ),
      ),
    );
  })(this.getAvailableFilterValues(OrderFilterId.Address));

  private getAssociatedAccounts = this.effect<void>((trigger$) => {
    return trigger$.pipe(
      switchMap(() =>
        combineLatest([
          this.activeAccount$,
          this.isMultiAccountEnabled$,
          this.basketAccounts$,
        ]).pipe(take(1)),
      ),
      switchMap(([activeAccount, isMultiAccountEnabled, basketAccounts]) => {
        if (!isMultiAccountEnabled) {
          return of([mapAccount(activeAccount)]);
        }
        return this.accountsService.getUserAssociatedAccounts().pipe(
          map((accounts) => {
            return [...accounts, ...basketAccounts].map(mapAccount);
          }),
          catchError(() => []),
        );
      }),
      tap((associatedAccounts: { accountId: string; name: string }[]) => {
        this.patchState((state) => ({
          ...state,
          associatedAccounts: associatedAccounts
            .map(({ accountId }) => accountId)
            .filter(unique),
          filtersNamesMap: {
            ...state.filtersNamesMap,
            [OrderFilterId.Account]: associatedAccounts.reduce(
              (acc, { accountId, name }) => ({
                ...acc,
                [accountId]: name,
              }),
              {},
            ),
          },
        }));
      }),
    );
  });

  public setInitialSelectedFilters$ = this.effect<Filter[]>(
    (availableFilters$) => {
      return availableFilters$.pipe(
        take(1),
        concatLatestFrom(() => [
          this.selectDefaultFilters$,
          this.store.pipe(select(selectQueryParams)),
        ]),
        map(([availableFilters, defaultFilters, params]) => {
          const filtersFromQuery = getInitialSelectedFiltersFromQueryParams(
            getAvailableFilters(defaultFilters, availableFilters),
            params,
            [OrderFilterId.InvoiceCreatedDate, OrderFilterId.PlacedDate],
          );
          return {
            searchQuery: params[ORDER_BOOK_SEARCH_URL_PARAM] ?? '',
            filters: filtersFromQuery.length
              ? filtersFromQuery
              : defaultFilters,
          };
        }),
        tap(({ searchQuery, filters }) => {
          this.patchState({
            searchQuery,
            selectedFilters: filters,
          });
        }),
      );
    },
  )(this.availableFilters$.pipe(filter(isNonNullish)));

  public updateActiveFilters(id: string, values: string[]) {
    this.patchState(({ selectedFilters }) => {
      return {
        selectedFilters: getUpdatedSelectedFilters(
          {
            id,
            values,
          },
          selectedFilters ?? [],
        ),
      };
    });
  }

  public updateSearchQuery(searchText: string) {
    this.patchState({
      searchQuery: searchText,
    });
  }

  public resetFilters() {
    this.selectDefaultFilters$
      .pipe(
        take(1),
        tap((selectedFilters: ActiveFilter[]) => {
          this.patchState({ selectedFilters });
        }),
      )
      .subscribe();
  }

  public initConfig(config: {
    filtersConfig: FilterConfig[];
    sortMapping: Record<string, SortRule[]>;
    defaultSortRule: SortRule;
    isMultiAccountEnabled: boolean;
    uiFilters?: Filter[];
  }) {
    this.patchState({
      ...config,
    });
  }

  private toggleSortDirection(current: SortRule | undefined, field: SortField) {
    if (!current || current.field !== field) {
      return SortDirection.Asc;
    }

    return current.direction === SortDirection.Asc
      ? SortDirection.Desc
      : SortDirection.Asc;
  }

  private mapSortValue(
    sort: SortRule,
    sortMapping: Record<string, SortRule[]> | undefined,
  ) {
    if (!sortMapping) {
      return [sort];
    }

    return sortMapping[`${sort.field}-${sort.direction}`];
  }

  private getAvailableFilterValues(
    filterId: OrderFilterId,
  ): Observable<string[]> {
    return this.availableFilters$.pipe(
      map((filters) => filters?.find(({ id }: Filter) => id === filterId)),
      filter(isNonNullish),
      take(1),
      map((filterItem) => filterItem.values.map(({ name }) => name)),
    );
  }
}

function mapAccount({ accountId, name }: Pick<Account, 'name' | 'accountId'>) {
  return {
    accountId,
    name,
  };
}
